import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { api } from '../../services/api';

// --- Types ---

interface Company {
  id: number;
  name: string;
  slug: string;
  email: string;
  legal_name: string | null;
  cuit: string | null;
  plan: string;
  is_active: boolean;
  created_at: string;
}

interface CompanyListResponse {
  data: Company[];
  meta: { total: number; page: number; page_size: number };
}

interface CompanyResponse {
  data: Company;
}

// --- Schemas ---

const createCompanySchema = z.object({
  name: z.string().min(1).max(200),
  legal_name: z.string().max(200).optional().or(z.literal('')),
  cuit: z.string().max(20).optional().or(z.literal('')),
  email: z.string().email(),
  admin_email: z.string().email(),
  admin_first_name: z.string().min(1).max(100),
  admin_last_name: z.string().min(1).max(100),
  admin_password: z.string().min(8).max(128),
});

type CreateCompanyForm = z.infer<typeof createCompanySchema>;

const editCompanySchema = z.object({
  name: z.string().min(1).max(200),
  legal_name: z.string().max(200).optional().or(z.literal('')),
  cuit: z.string().max(20).optional().or(z.literal('')),
  email: z.string().email(),
});

type EditCompanyForm = z.infer<typeof editCompanySchema>;

// --- Component ---

export default function CompaniesPage(): React.ReactElement {
  const { t } = useTranslation('companies');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; company: Company | null }>({
    open: false,
    company: null,
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const createModalRef = useRef<HTMLDialogElement>(null);
  const editModalRef = useRef<HTMLDialogElement>(null);
  const confirmModalRef = useRef<HTMLDialogElement>(null);

  // --- Auto-dismiss snackbar ---
  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [snackbar.open]);

  // --- Sync modals ---
  useEffect(() => {
    if (createOpen) createModalRef.current?.showModal();
    else createModalRef.current?.close();
  }, [createOpen]);

  useEffect(() => {
    if (editOpen) editModalRef.current?.showModal();
    else editModalRef.current?.close();
  }, [editOpen]);

  useEffect(() => {
    if (confirmDialog.open) confirmModalRef.current?.showModal();
    else confirmModalRef.current?.close();
  }, [confirmDialog.open]);

  // --- Queries ---

  const { data: companiesData, isLoading } = useQuery({
    queryKey: ['admin-companies', paginationModel.page, paginationModel.pageSize, search],
    queryFn: () =>
      api.get<CompanyListResponse>('/v1/admin/companies', {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        ...(search ? { search } : {}),
      }),
  });

  const companies = companiesData?.data ?? [];
  const totalRows = companiesData?.meta?.total ?? 0;
  const totalPages = Math.ceil(totalRows / paginationModel.pageSize);

  // --- Mutations ---

  const createMutation = useMutation({
    mutationFn: (data: CreateCompanyForm) => api.post<CompanyResponse>('/v1/admin/companies', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
      setCreateOpen(false);
      createForm.reset();
      setSnackbar({ open: true, message: t('messages.createSuccess'), severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: t('messages.error'), severity: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: EditCompanyForm }) =>
      api.patch<CompanyResponse>(`/v1/admin/companies/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
      setEditOpen(false);
      setEditingCompany(null);
      setSnackbar({ open: true, message: t('messages.updateSuccess'), severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: t('messages.error'), severity: 'error' });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) => {
      if (is_active) {
        return api.patch<CompanyResponse>(`/v1/admin/companies/${id}`, { is_active: false });
      }
      return api.patch<CompanyResponse>(`/v1/admin/companies/${id}`, { is_active: true });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
      const msgKey = variables.is_active ? 'messages.deactivateSuccess' : 'messages.reactivateSuccess';
      setSnackbar({ open: true, message: t(msgKey), severity: 'success' });
      setConfirmDialog({ open: false, company: null });
    },
    onError: () => {
      setSnackbar({ open: true, message: t('messages.error'), severity: 'error' });
      setConfirmDialog({ open: false, company: null });
    },
  });

  // --- Forms ---

  const createForm = useForm<CreateCompanyForm>({
    resolver: zodResolver(createCompanySchema),
    defaultValues: {
      name: '',
      legal_name: '',
      cuit: '',
      email: '',
      admin_email: '',
      admin_first_name: '',
      admin_last_name: '',
      admin_password: '',
    },
  });

  const editForm = useForm<EditCompanyForm>({
    resolver: zodResolver(editCompanySchema),
  });

  const handleEdit = (company: Company): void => {
    setEditingCompany(company);
    editForm.reset({
      name: company.name,
      legal_name: company.legal_name || '',
      cuit: company.cuit || '',
      email: company.email,
    });
    setEditOpen(true);
  };

  const handleToggleActive = (company: Company): void => {
    setConfirmDialog({ open: true, company });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">{t('title')}</h1>
          <p className="text-sm text-base-content/60">{t('subtitle')}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          {t('createCompany')}
        </button>
      </div>

      {/* Search */}
      <input
        type="text"
        className="input input-bordered w-full mb-4"
        placeholder={t('searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* Table */}
      <div className="overflow-x-auto bg-base-100 rounded-lg">
        {isLoading ? (
          <div className="flex justify-center p-8">
            <span className="loading loading-spinner loading-lg" />
          </div>
        ) : (
          <table className="table table-zebra">
            <thead>
              <tr>
                <th>{t('columns.name')}</th>
                <th>{t('columns.slug')}</th>
                <th>{t('columns.email')}</th>
                <th>{t('columns.plan')}</th>
                <th>{t('columns.status')}</th>
                <th>{t('columns.createdAt')}</th>
                <th>{t('columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {companies.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-base-content/60 py-8">
                    {tCommon('common.noResults')}
                  </td>
                </tr>
              ) : (
                companies.map((company) => (
                  <tr key={company.id}>
                    <td className="font-medium">{company.name}</td>
                    <td>{company.slug}</td>
                    <td>{company.email}</td>
                    <td>
                      <span className="badge badge-outline">{t(`plans.${company.plan}`, { defaultValue: company.plan })}</span>
                    </td>
                    <td>
                      <span className={`badge badge-sm ${company.is_active ? 'badge-success' : 'badge-ghost'}`}>
                        {company.is_active ? tCommon('common.active') : tCommon('common.inactive')}
                      </span>
                    </td>
                    <td>{company.created_at ? new Date(company.created_at).toLocaleDateString() : ''}</td>
                    <td>
                      <div className="flex gap-1">
                        <button
                          className="btn btn-ghost btn-xs"
                          title={t('editCompany')}
                          onClick={() => handleEdit(company)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>
                        <button
                          className="btn btn-ghost btn-xs"
                          title={company.is_active ? t('deactivate') : t('reactivate')}
                          onClick={() => handleToggleActive(company)}
                        >
                          {company.is_active ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-error" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-success" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalRows > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-base-200">
            <span className="text-sm text-base-content/60">
              {t('columns.page', {
                page: paginationModel.page + 1,
                total: totalPages,
              })}
            </span>
            <div className="join">
              <button
                className="join-item btn btn-sm"
                disabled={paginationModel.page === 0}
                onClick={() => setPaginationModel((prev) => ({ ...prev, page: prev.page - 1 }))}
              >
                «
              </button>
              <button className="join-item btn btn-sm">
                {paginationModel.page + 1}
              </button>
              <button
                className="join-item btn btn-sm"
                disabled={paginationModel.page >= totalPages - 1}
                onClick={() => setPaginationModel((prev) => ({ ...prev, page: prev.page + 1 }))}
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Company Modal */}
      <dialog ref={createModalRef} className="modal" onClose={() => setCreateOpen(false)}>
        <div className="modal-box">
          <h3 className="font-bold text-lg mb-4">{t('createCompany')}</h3>
          <form onSubmit={createForm.handleSubmit((data) => createMutation.mutate(data))} noValidate>
            <div className="form-control w-full mb-3">
              <label className="label"><span className="label-text">{t('form.name')}</span></label>
              <input
                type="text"
                className={`input input-bordered w-full ${createForm.formState.errors.name ? 'input-error' : ''}`}
                {...createForm.register('name')}
              />
              {createForm.formState.errors.name && (
                <label className="label"><span className="label-text-alt text-error">{createForm.formState.errors.name.message}</span></label>
              )}
            </div>
            <div className="form-control w-full mb-3">
              <label className="label"><span className="label-text">{t('form.legalName')}</span></label>
              <input
                type="text"
                className={`input input-bordered w-full ${createForm.formState.errors.legal_name ? 'input-error' : ''}`}
                {...createForm.register('legal_name')}
              />
              {createForm.formState.errors.legal_name && (
                <label className="label"><span className="label-text-alt text-error">{createForm.formState.errors.legal_name.message}</span></label>
              )}
            </div>
            <div className="form-control w-full mb-3">
              <label className="label"><span className="label-text">{t('form.cuit')}</span></label>
              <input
                type="text"
                className={`input input-bordered w-full ${createForm.formState.errors.cuit ? 'input-error' : ''}`}
                {...createForm.register('cuit')}
              />
              {createForm.formState.errors.cuit && (
                <label className="label"><span className="label-text-alt text-error">{createForm.formState.errors.cuit.message}</span></label>
              )}
            </div>
            <div className="form-control w-full mb-3">
              <label className="label"><span className="label-text">{t('form.email')}</span></label>
              <input
                type="email"
                className={`input input-bordered w-full ${createForm.formState.errors.email ? 'input-error' : ''}`}
                {...createForm.register('email')}
              />
              {createForm.formState.errors.email && (
                <label className="label"><span className="label-text-alt text-error">{createForm.formState.errors.email.message}</span></label>
              )}
            </div>
            <div className="form-control w-full mb-3">
              <label className="label"><span className="label-text">{t('form.adminEmail')}</span></label>
              <input
                type="email"
                className={`input input-bordered w-full ${createForm.formState.errors.admin_email ? 'input-error' : ''}`}
                {...createForm.register('admin_email')}
              />
              {createForm.formState.errors.admin_email && (
                <label className="label"><span className="label-text-alt text-error">{createForm.formState.errors.admin_email.message}</span></label>
              )}
            </div>
            <div className="form-control w-full mb-3">
              <label className="label"><span className="label-text">{t('form.adminFirstName')}</span></label>
              <input
                type="text"
                className={`input input-bordered w-full ${createForm.formState.errors.admin_first_name ? 'input-error' : ''}`}
                {...createForm.register('admin_first_name')}
              />
              {createForm.formState.errors.admin_first_name && (
                <label className="label"><span className="label-text-alt text-error">{createForm.formState.errors.admin_first_name.message}</span></label>
              )}
            </div>
            <div className="form-control w-full mb-3">
              <label className="label"><span className="label-text">{t('form.adminLastName')}</span></label>
              <input
                type="text"
                className={`input input-bordered w-full ${createForm.formState.errors.admin_last_name ? 'input-error' : ''}`}
                {...createForm.register('admin_last_name')}
              />
              {createForm.formState.errors.admin_last_name && (
                <label className="label"><span className="label-text-alt text-error">{createForm.formState.errors.admin_last_name.message}</span></label>
              )}
            </div>
            <div className="form-control w-full">
              <label className="label"><span className="label-text">{t('form.adminPassword')}</span></label>
              <input
                type="password"
                className={`input input-bordered w-full ${createForm.formState.errors.admin_password ? 'input-error' : ''}`}
                placeholder="Min. 8 caracteres"
                {...createForm.register('admin_password')}
              />
              {createForm.formState.errors.admin_password && (
                <label className="label"><span className="label-text-alt text-error">{createForm.formState.errors.admin_password.message}</span></label>
              )}
            </div>
            <div className="modal-action">
              <button type="button" className="btn" onClick={() => setCreateOpen(false)}>
                {tCommon('buttons.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>
                {createMutation.isPending ? <span className="loading loading-spinner loading-sm" /> : t('createCompany')}
              </button>
            </div>
          </form>
        </div>
        <form method="dialog" className="modal-backdrop"><button onClick={() => setCreateOpen(false)}>close</button></form>
      </dialog>

      {/* Edit Company Modal */}
      <dialog ref={editModalRef} className="modal" onClose={() => setEditOpen(false)}>
        <div className="modal-box">
          <h3 className="font-bold text-lg mb-4">{t('editCompany')}</h3>
          <form
            onSubmit={editForm.handleSubmit((data) => {
              if (editingCompany) updateMutation.mutate({ id: editingCompany.id, data });
            })}
            noValidate
          >
            <div className="form-control w-full mb-3">
              <label className="label"><span className="label-text">{t('form.name')}</span></label>
              <input
                type="text"
                className={`input input-bordered w-full ${editForm.formState.errors.name ? 'input-error' : ''}`}
                {...editForm.register('name')}
              />
              {editForm.formState.errors.name && (
                <label className="label"><span className="label-text-alt text-error">{editForm.formState.errors.name.message}</span></label>
              )}
            </div>
            <div className="form-control w-full mb-3">
              <label className="label"><span className="label-text">{t('form.legalName')}</span></label>
              <input
                type="text"
                className={`input input-bordered w-full ${editForm.formState.errors.legal_name ? 'input-error' : ''}`}
                {...editForm.register('legal_name')}
              />
              {editForm.formState.errors.legal_name && (
                <label className="label"><span className="label-text-alt text-error">{editForm.formState.errors.legal_name.message}</span></label>
              )}
            </div>
            <div className="form-control w-full mb-3">
              <label className="label"><span className="label-text">{t('form.cuit')}</span></label>
              <input
                type="text"
                className={`input input-bordered w-full ${editForm.formState.errors.cuit ? 'input-error' : ''}`}
                {...editForm.register('cuit')}
              />
              {editForm.formState.errors.cuit && (
                <label className="label"><span className="label-text-alt text-error">{editForm.formState.errors.cuit.message}</span></label>
              )}
            </div>
            <div className="form-control w-full mb-3">
              <label className="label"><span className="label-text">{t('form.email')}</span></label>
              <input
                type="email"
                className={`input input-bordered w-full ${editForm.formState.errors.email ? 'input-error' : ''}`}
                {...editForm.register('email')}
              />
              {editForm.formState.errors.email && (
                <label className="label"><span className="label-text-alt text-error">{editForm.formState.errors.email.message}</span></label>
              )}
            </div>
            <div className="modal-action">
              <button type="button" className="btn" onClick={() => setEditOpen(false)}>
                {tCommon('buttons.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? <span className="loading loading-spinner loading-sm" /> : tCommon('buttons.save')}
              </button>
            </div>
          </form>
        </div>
        <form method="dialog" className="modal-backdrop"><button onClick={() => setEditOpen(false)}>close</button></form>
      </dialog>

      {/* Confirm Deactivate/Reactivate Modal */}
      <dialog ref={confirmModalRef} className="modal" onClose={() => setConfirmDialog({ open: false, company: null })}>
        <div className="modal-box">
          <h3 className="font-bold text-lg">
            {confirmDialog.company?.is_active ? t('deactivate') : t('reactivate')}
          </h3>
          <p className="py-4 text-base-content/70">
            {confirmDialog.company?.is_active
              ? t('confirmDeactivate', { name: confirmDialog.company?.name })
              : t('confirmReactivate', { name: confirmDialog.company?.name })}
          </p>
          <div className="modal-action">
            <button className="btn" onClick={() => setConfirmDialog({ open: false, company: null })}>
              {tCommon('buttons.cancel')}
            </button>
            <button
              className={`btn ${confirmDialog.company?.is_active ? 'btn-error' : 'btn-success'}`}
              disabled={toggleActiveMutation.isPending}
              onClick={() => {
                if (confirmDialog.company) {
                  toggleActiveMutation.mutate({
                    id: confirmDialog.company.id,
                    is_active: confirmDialog.company.is_active,
                  });
                }
              }}
            >
              {toggleActiveMutation.isPending
                ? <span className="loading loading-spinner loading-sm" />
                : confirmDialog.company?.is_active
                  ? t('deactivate')
                  : t('reactivate')}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setConfirmDialog({ open: false, company: null })}>close</button>
        </form>
      </dialog>

      {/* Toast / Snackbar */}
      {snackbar.open && (
        <div className="toast toast-end toast-bottom z-50">
          <div className={`alert ${snackbar.severity === 'success' ? 'alert-success' : 'alert-error'}`}>
            <span>{snackbar.message}</span>
            <button className="btn btn-ghost btn-xs" onClick={() => setSnackbar((s) => ({ ...s, open: false }))}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
